import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import MojitoCore from 'mojito/core';
import serviceFactory from 'services/bonus/service/service-factory.js';
import BonusTypes from 'services/bonus/types.js';
import { isLoggedIn as isLoggedInSelector } from 'services/authentication/selectors.js';
import { actions as authenticationActions } from 'services/authentication/slice.js';
import { actions as bootstrapActions } from 'services/bootstrap/slice.js';
import ServicesTypes from 'services/common/types.js';

const reduxInstance = MojitoCore.Services.redux;
const log = MojitoCore.logger.get('BonusCampaignStore');
const { BONUS_FILTER } = BonusTypes;
const { UNKNOWN, AVAILABLE, UNAVAILABLE, PENDING } = ServicesTypes.CONTENT_STATE;

/**
 * Defines the state of the bonus campaign tracking store.
 *
 * @typedef BonusCampaignState
 *
 * @property {Mojito.Services.Common.types.CONTENT_STATE} loadingState - Campaigns loading state.
 * @property {object} campaignsByFilter - Mapping {Mojito.Services.Bonus.types.BONUS_FILTER} to {Mojito.Services.Bonus.types.Campaign}.
 *
 * @memberof Mojito.Services.BonusCampaign
 */

/**
 * The name of the bonus campaign store. It is used to register in global redux store.
 *
 * @constant
 * @type {string}
 * @memberof Mojito.Services.BonusCampaign.slice
 */
export const STORE_KEY = 'bonusCampaignStore';

export const initialState = {
    loadingState: UNKNOWN,
    campaignsByFilter: {},
};

/**
 * Reset store to initial state.
 *
 * @function reset
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Services.BonusCampaign.actions
 */

/**
 * Campaigns pending.
 *
 * @function campaignBonusesPending
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Services.BonusCampaign.actions
 */

/**
 * Campaigns successfully fetched.
 *
 * @function getCampaignsSuccess
 * @type {Mojito.Core.Services.redux.ActionCreator}
 * @param {{campaigns: Array<Mojito.Services.Bonus.types.CAMPAIGN>, inProgressBonuses: Array<Mojito.Services.Bonus.types.InProgressBonus>, filter: Mojito.Services.Bonus.types.BONUS_FILTER, isLoggedIn: boolean}} payload - Campaign data.
 *
 * @memberof Mojito.Services.BonusCampaign.actions
 */

/**
 * Get campaigns failed.
 *
 * @function getCampaignsFailed
 * @type {Mojito.Core.Services.redux.ActionCreator}
 *
 * @memberof Mojito.Services.BonusCampaign.actions
 */

export const { reducer, actions } = createSlice({
    name: STORE_KEY,
    initialState,

    reducers: {
        reset() {
            return { ...initialState };
        },
        campaignBonusesPending(state) {
            state.loadingState = PENDING;
        },
        getCampaignsSuccess(state, { payload }) {
            const { campaigns, inProgressBonuses, filter, isLoggedIn } = payload;
            const { HISTORIC, IN_PROGRESS, ACTIVE } = BONUS_FILTER;

            state.loadingState = AVAILABLE;
            if (isLoggedIn) {
                if (filter === HISTORIC) {
                    state.campaignsByFilter[HISTORIC] = campaigns || [];
                } else {
                    state.campaignsByFilter[IN_PROGRESS] = inProgressBonuses || [];
                    state.campaignsByFilter[ACTIVE] = campaigns || [];
                }
            }
        },
        getCampaignsFailed(state) {
            state.loadingState = UNAVAILABLE;
        },
    },
});

/**
 * Get campaigns.
 *
 * @function getCampaigns
 *
 * @param {Mojito.Services.Bonus.types.BONUS_FILTER} filter - Campaign filter option.
 * @returns {Mojito.Core.Services.redux.ThunkFunction} Get campaigns thunk.
 *
 * @memberof Mojito.Services.BonusCampaign.actions
 */
actions.getCampaigns = filter => (dispatch, getState) => {
    dispatch(actions.campaignBonusesPending());

    serviceFactory
        .getService()
        .getCampaigns(filter)
        .then(({ campaigns, inProgressBonuses }) => {
            const isLoggedIn = isLoggedInSelector(getState());
            dispatch(
                actions.getCampaignsSuccess({ campaigns, inProgressBonuses, filter, isLoggedIn })
            );
        })
        .catch(({ message, type } = {}) => {
            log.warn(`Fetching of campaigns failed: ${type} ${message}`);
            dispatch(actions.getCampaignsFailed());
        });
};

reduxInstance.actionListener.startListening({
    matcher: isAnyOf(bootstrapActions.dispose, authenticationActions.disposeSession),
    effect: (action, listenerApi) => listenerApi.dispatch(actions.reset()),
});

reduxInstance.injectReducer(STORE_KEY, reducer);
